import { useState } from "react";
import SlabMock from "../serials/tags/SlabMock";
import { ArtFacePreview } from "../serials/tags/LabelCardEditor";
import { artFacePt } from "../serials/tags/layout/artGeometryPt";
import { methodLabel } from "./variationRank";
import { previewCard, modelOptionLabel, SAMPLE_CONDITION } from "./previewCard";
import { useLinkedModels } from "./useLinkedModels";
import { useContainerWidth } from "./useContainerWidth";

const MAX_SLAB_PX = 440;
const GAP_PX = 16;

// Correção não tem provider de verdade ("local"): o rótulo basta.
const variationCaption = (v) => (v.gen.method === "correction"
    ? methodLabel(v.gen.method)
    : [methodLabel(v.gen.method), v.gen.provider].filter(Boolean).join(" · "));

/** Faixa de variações (geradas e corrigidas): selo recomendada, motivos curtos, clique seleciona. */
export const VariationStrip = ({ ranked, selected, onSelect }) => (
    <div className="flex flex-wrap gap-2 mt-2" data-testid="variation-strip">
        {ranked.map((v) => {
            const active = Boolean(selected && selected.generationId === v.generationId && selected.outputIndex === v.outputIndex);
            return (
                <button
                    key={`${v.generationId}-${v.outputIndex}`}
                    type="button"
                    aria-pressed={active}
                    onClick={() => onSelect(v.generationId, v.outputIndex)}
                    className={`p-0 cursor-pointer border-2 text-left ${active ? "border-[#d59106]" : "border-transparent"} ${v.rejected ? "opacity-40" : ""}`}
                >
                    <img src={v.output.url} alt={`variação ${v.outputIndex + 1} da geração ${v.generationId}`} className="h-20 block" />
                    <div className="text-[10px] leading-tight max-w-[10rem]">
                        {v.recommended ? <span className="bg-green-700 text-white px-1 mr-1">recomendada</span> : null}
                        <span className="text-[#d59106]">{variationCaption(v)}</span>
                        {v.score !== null ? <span className="text-gray-400"> · {v.score.toFixed(2).replace(".", ",")}</span> : null}
                        {v.reasons.length ? <div className="text-gray-400">{v.reasons.join(" · ")}</div> : null}
                    </div>
                </button>
            );
        })}
        {!ranked.length ? <p className="text-gray-500">Nenhuma variação ainda. Gere à direita ou envie uma arte corrigida.</p> : null}
    </div>
);

/**
 * Continuação completa (outpaint-up): o canvas inteiro que o provider pintou — etiqueta em cima,
 * ilustração da carta continuada embaixo. O slab já mostra o recorte na janela da etiqueta e a
 * carta real abaixo (como sai na cápsula); aqui a faixa tracejada marca o que vira etiqueta, para o
 * revisor julgar a emenda entre a arte nova e a ilustração original.
 */
export const ContinuityPreview = ({ output }) => {
    if (!output?.fullUrl) return null;
    const labelH = output.gen?.inputs?.labelCanvas?.height || output.height;
    const fullH = output.fullHeight || output.gen?.inputs?.canvas?.height;
    const cutPct = labelH && fullH ? Math.min(100, (labelH / fullH) * 100) : null;
    return (
        <figure data-testid="continuity-full" className="m-0 mt-3 max-w-[220px]">
            <div className="relative">
                <img src={output.fullUrl} alt="continuação completa da variação selecionada" className="block w-full border border-[#444]" />
                {cutPct !== null ? (
                    <div data-testid="continuity-cut" className="absolute left-0 top-0 w-full border-2 border-dashed border-[#d59106] pointer-events-none" style={{ height: `${cutPct}%` }} />
                ) : null}
            </div>
            <figcaption className="text-xs text-gray-400 mt-1">Continuação completa: o tracejado é o recorte da etiqueta; abaixo, a ilustração da carta continuada</figcaption>
        </figure>
    );
};

/**
 * Coluna esquerda da revisão (spec 2026-10-01 §6): slab simulado com a variação selecionada,
 * "Visualizar com" (modelo ligado que dá o texto das placas), guias e comparação com a aprovada.
 */
const ReviewSlabPanel = ({ label, geometry, token, ranked, selected, selectedOutput, onSelect }) => {
    const models = useLinkedModels(token, label.gradableModels);
    // null = automático (primeiro modelo ligado); "" = exemplo escolhido pelo operador
    const [modelChoice, setModelChoice] = useState(null);
    const [guides, setGuides] = useState(false);
    const [compare, setCompare] = useState(false);
    const [boxRef, boxWidth] = useContainerWidth();

    const modelId = modelChoice ?? (models[0] ? String(models[0]._id) : "");
    const model = models.find((m) => String(m._id) === modelId) || null;
    const cardImageUrl = label.sourceImage?.signedUrl || label.sourceImage?.url || null;
    const approvedUrl = label.approved?.url || null;
    const showCompare = compare && Boolean(approvedUrl);
    const slabWidth = Math.max(160, Math.min(MAX_SLAB_PX, showCompare ? (boxWidth - GAP_PX) / 2 : boxWidth));

    const slab = (artUrl, testId, caption) => (
        <figure data-testid={testId} className="m-0">
            <SlabMock geometry={geometry} width={slabWidth} faceSize={artFacePt(geometry)} cardImageUrl={cardImageUrl} guides={guides}>
                <ArtFacePreview card={previewCard({ model, art: label, artUrl, cardImageUrl })} geometry={geometry} />
            </SlabMock>
            <figcaption className="text-xs text-gray-400 mt-1">{caption}</figcaption>
        </figure>
    );

    return (
        <div>
            <div className="flex flex-wrap items-center gap-3 mb-2">
                <label>Visualizar com
                    <select aria-label="Visualizar com" className="text-black ml-2" value={modelId} onChange={(e) => setModelChoice(e.target.value)}>
                        <option value="">exemplo (sem modelo)</option>
                        {models.map((m) => <option key={String(m._id)} value={String(m._id)}>{modelOptionLabel(m)}</option>)}
                    </select>
                </label>
                <label>
                    <input type="checkbox" checked={guides} onChange={(e) => setGuides(e.target.checked)} /> Guias (sangria e área segura)
                </label>
                <label title={approvedUrl ? undefined : "Nenhuma versão aprovada"}>
                    <input type="checkbox" checked={showCompare} disabled={!approvedUrl} onChange={(e) => setCompare(e.target.checked)} /> Comparar com a aprovada
                </label>
            </div>
            <div ref={boxRef} className="flex gap-4 items-start">
                {slab(selectedOutput?.url || null, "slab-selected", selectedOutput ? `Variação selecionada · condição de exemplo ${SAMPLE_CONDITION}` : "Nenhuma variação selecionada")}
                {showCompare ? slab(approvedUrl, "slab-approved", `Aprovada v${label.approved.version}`) : null}
            </div>
            <ContinuityPreview output={selectedOutput} />
            <VariationStrip ranked={ranked} selected={selected} onSelect={onSelect} />
        </div>
    );
};

export default ReviewSlabPanel;
